import React, { Component, Fragment } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { FormattedMessage } from 'react-intl';

import {
  displayToaster, hideToaster, toggleDrawer, getEmployees,
  UPLOAD_EMPLOYEE_DOCUMENT, clearLoadingState, saveTemplateMetadata,
  uploadEmployeeDocument, getCompanies, clearEmployeeData,
  getEmployeeDocCategories, GET_EMPLOYEE_DOCUMENT_CATEGORIES,
  GET_COMPANIES, GET_EMPLOYEES, searchEmployees, clearEmployeeSearchData,
  LOADING_PROP_TYPE, clearCategories,
} from '../../modules';
import { Drawer, DrawerHeader, DocumentUploadForm } from '../../components';
import { APPLICATION_ROOT } from '../../constants';

const DEFAULT_STATE = {
  file: undefined,
  drawerClasses: ['drawer'],
};

class UploadDocument extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...DEFAULT_STATE,
      selectedCompanyId: get(window, 'asure.appSettings.selectedCompany.companyId'),
      selectedEmployeeId: get(window, 'asure.appSettings.selectedEmployee.employeeId'),
    };

    this.onUpload = this.onUpload.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.handleDrawer = this.handleDrawer.bind(this);
  }

  componentDidMount() {
    const { companies: { results: companyData }, employees: { results: employeeData } } = this.props;
    const { selectedCompanyId, selectedEmployeeId } = this.state;
    // ensure that we have employee data
    if (!employeeData.length && !selectedEmployeeId && selectedCompanyId) this.props.getEmployees();
    // ensure that we have company data
    if (!companyData.length && !selectedCompanyId) this.props.getCompanies();
    if (selectedCompanyId && selectedEmployeeId) {
      this.props.getEmployeeDocCategories(undefined, selectedCompanyId, selectedEmployeeId);
    } else if (selectedCompanyId) {
      this.props.getEmployeeDocCategories(undefined, selectedCompanyId);
    }
  }

  componentDidUpdate(prevProps) {
    const { loading, drawerIsOpen, docCategories: { categories } } = this.props;
    const { loading: prevLoading, drawerIsOpen: prevDrawerIsOpen } = prevProps;
    if (prevLoading[UPLOAD_EMPLOYEE_DOCUMENT] === 'loading' && loading[UPLOAD_EMPLOYEE_DOCUMENT] === 'complete') {
      const { file: { file: { path: fileName } } } = this.state;
      this.props.displayToaster([
        'success',
        <FormattedMessage id="SUCCESSFULLY_UPLOADED" values={{ fileName }} defaultMessage="FILE_UPLOADED" />,
        'app',
        5000,
        true,
      ]);
      this.handleDrawer();
    }
    if (prevDrawerIsOpen && !drawerIsOpen && categories.length > 0) {
      this.props.clearCategories();
    }
  }

  onUpload(title, employeeId, companyId, isPrivate = false, category) {
    const { file } = this.state;
    file.title = title;
    this.props.uploadEmployeeDocument(file, employeeId, companyId, category, isPrivate);
  }

  onDrop(file) {
    this.setState({ file });
  }

  loadMoreData = (key, functionName, companyId, actionType, query) => {
    const { [key]: { next }, loading } = this.props;
    const { selectedCompanyId } = this.state;
    if (!selectedCompanyId && companyId && key === 'employees') {
      this.props.clearEmployeeData();
      this.props.getEmployees('', companyId);
    } else {
      if ((!next && !query) || loading[actionType] === 'loading') return;
      this.props[functionName](next, '', query);
    }
  };

  searchEmployees(query, companyId) {
    const { selectedCompanyId } = this.state;
    if (query) {
      this.props.searchEmployees(selectedCompanyId || companyId, query);
    } else {
      this.props.clearEmployeeSearchData();
    }
  }

  handleDrawer() {
    const { drawerIsOpen } = this.props;
    this.props.toggleDrawer(!drawerIsOpen);
    this.props.hideToaster('drawer');
    this.props.clearLoadingState(UPLOAD_EMPLOYEE_DOCUMENT);
    if (!this.state.selectedCompanyId) {
      this.props.clearEmployeeData();
    }
    // Allowing the drawer to close before resetting state makes for a cleaner animation
    setTimeout(() => this.setState(DEFAULT_STATE), 500);
  }

  loadMoreCategories(companyId) {
    const { docCategories: { next } } = this.props;
    if (next && this.props.loading[GET_EMPLOYEE_DOCUMENT_CATEGORIES] === 'complete') {
      this.props.getEmployeeDocCategories(next, companyId);
    }
  }

  loadCategories(companyId) {
    this.props.getEmployeeDocCategories(undefined, companyId);
  }

  groupCategories = (docCategories) => {
    const clearCategory = { label: '   ', value: '' };
    const { recent, categories } = docCategories;
    return [
      { label: 'RECENT', options: recent },
      { label: 'CATEGORIES', options: [clearCategory, ...categories] },
    ];
  };

  render() {
    const { file, drawerClasses } = this.state;
    const {
      loading, drawerIsOpen,
      employees: { results: employees, searchResults: employeeSearchResults },
      companies: { results: companies },
      userRoles,
      docCategories,
    } = this.props;
    return (
      <Fragment>
        <Drawer
          modalClassName={drawerClasses.join(' ')}
          toggle={this.handleDrawer}
          isOpen={drawerIsOpen}
          parentselector={APPLICATION_ROOT}
          backdrop={loading[UPLOAD_EMPLOYEE_DOCUMENT] === 'loading' ? 'static' : true}
        >
          <DrawerHeader
            title="ADD_DOCUMENT"
            toggle={loading[UPLOAD_EMPLOYEE_DOCUMENT] !== 'loading' ? this.handleDrawer.bind(this) : () => {}}
          />
          <DocumentUploadForm
            onUpload={this.onUpload}
            onDrop={this.onDrop.bind(this)}
            onError={this.props.displayToaster}
            hasFile={!!file}
            loading={loading}
            userRoles={userRoles}
            data={{
              employees,
              companies,
              employeeSearchResults,
              groupedCategoryList: this.groupCategories(docCategories),
            }}
            functions={{
              getEmployees: (query, companyId) => this.loadMoreData('employees', 'getEmployees', companyId, GET_EMPLOYEES, query),
              getCompanies: this.loadMoreData.bind(this, 'companies', 'getCompanies', null, GET_COMPANIES),
              searchEmployees: (query, companyId) => this.searchEmployees(query, companyId),
              getCategories: companyId => this.loadMoreCategories(companyId),
              loadCategories: companyId => this.loadCategories(companyId),
            }}
          />
        </Drawer>
      </Fragment>
    );
  }
}

UploadDocument.defaultProps = {
  drawerIsOpen: false,
  docCategories: undefined,
};

UploadDocument.propTypes = {
  displayToaster: PropTypes.func.isRequired,
  loading: LOADING_PROP_TYPE.isRequired,
  drawerIsOpen: PropTypes.bool,
  toggleDrawer: PropTypes.func.isRequired,
  hideToaster: PropTypes.func.isRequired,
  clearLoadingState: PropTypes.func.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  employees: PropTypes.shape({
    results: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })),
    next: PropTypes.string,
    searchResults: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })),
  }).isRequired,
  companies: PropTypes.shape({
    results: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })),
    next: PropTypes.string,
  }).isRequired,
  employeeDocs: PropTypes.shape({
    results: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      category: PropTypes.string,
      uploadDate: PropTypes.string,
      isEsignatureDocument: PropTypes.bool,
      isHelloSignTemplate: PropTypes.bool,
      fileName: PropTypes.string,
      extension: PropTypes.string,
    })),
  }).isRequired,
  getEmployees: PropTypes.func.isRequired,
  uploadEmployeeDocument: PropTypes.func.isRequired,
  getCompanies: PropTypes.func.isRequired,
  clearEmployeeData: PropTypes.func.isRequired,
  searchEmployees: PropTypes.func.isRequired,
  clearEmployeeSearchData: PropTypes.func.isRequired,
  getEmployeeDocCategories: PropTypes.func.isRequired,
  clearCategories: PropTypes.func.isRequired,
  docCategories: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })),
    recent: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })),
    next: PropTypes.string,
  }),
};

export default connect(
  (state) => {
    const { appState, resources: { employees, companies, docs: { employeeDocs, docCategories } }, user: { roles } } = state;
    return {
      loading: appState.loading,
      drawerIsOpen: appState.drawer.isOpen,
      userRoles: roles,
      employees,
      employeeDocs,
      companies,
      docCategories,
    };
  },
  {
    displayToaster: args => displayToaster(...args),
    toggleDrawer: open => toggleDrawer(open),
    hideToaster: location => hideToaster(location),
    clearLoadingState: action => clearLoadingState(action),
    saveTemplateMetadata: postBody => saveTemplateMetadata(postBody),
    getEmployees,
    uploadEmployeeDocument,
    getCompanies,
    clearEmployeeData,
    searchEmployees,
    clearEmployeeSearchData,
    getEmployeeDocCategories,
    clearCategories,
  }
)(hot(module)(UploadDocument));



// WEBPACK FOOTER //
// ./src/containers/UploadDocument/UploadDocument.jsx